import {
  selectBalancingWorkstation,
  selectBalancingSession,
  getRegisterHistoryFilters,
} from '@/lib/services/cash-drawers/cash-drawers.balancing'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice, InfoNoticeAction } from '@/components/patterns/info-notice'
import { LoadingState } from '@/components/patterns/loading-state'
import { AdjustDrawerDialog } from '@/components/pos/CashDrawerDialogs'
import { CashDrawerRegisterBalancing } from '@/components/pos/CashDrawerRegisterBalancing'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { Can, useAbility } from '@/lib/casl'
import { useCashDrawerDevicePairing } from '@/lib/services/cash-drawers/cash-drawer-device'
import {
  cashDrawerQueries,
  useCashDrawerStatus,
  useCashDrawerWorkstations,
  useOpenCashDrawers,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import { cashDrawerBalancingSearchSchema } from '@/lib/services/cash-drawers/cash-drawers.schema'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import { isGlobalOrganization } from '@/lib/user-access'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { Banknote, History, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/money/cash-drawer-balancing')({
  component: CashDrawerBalancingPage,
  head: () => adminTitleHead('Cash Drawer Balancing'),
  validateSearch: (search) => cashDrawerBalancingSearchSchema.parse(search),
  loader: async ({ context, params }) => {
    if (!context.ability.can('operate', 'cash-drawers')) throw notFound()

    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )

    if (isGlobalOrganization(organization)) throw notFound()

    const status = await context.queryClient.ensureQueryData(
      cashDrawerQueries.status({ organizationId: params.orgId })
    )
    if (status.enabled) {
      await Promise.all([
        context.queryClient.ensureQueryData(
          cashDrawerQueries.workstations({ organizationId: params.orgId })
        ),
        context.queryClient.ensureQueryData(
          cashDrawerQueries.open({ organizationId: params.orgId })
        ),
      ])
    }

    return { isReadOnly: false }
  },
})

export function CashDrawerBalancingPage() {
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const ability = useAbility()
  const navigate = useNavigate({ from: Route.fullPath })
  const path = usePathHelper()
  const { data: organization } = useOrganization(orgId)
  const statusQuery = useCashDrawerStatus({ organizationId: orgId })
  const trackingEnabled = statusQuery.data?.enabled === true && !statusQuery.isError
  const workstationsQuery = useCashDrawerWorkstations({
    organizationId: orgId,
    enabled: trackingEnabled,
  })
  const openDrawersQuery = useOpenCashDrawers({ organizationId: orgId, enabled: trackingEnabled })
  const canViewHistory = ability.can('list', 'cash-drawer-reports')
  const { workstationId: pairedWorkstationId } = useCashDrawerDevicePairing(orgId)
  const [adjustingSessionId, setAdjustingSessionId] = useState(null)
  const workstations = (workstationsQuery.data ?? []).filter(
    (workstation) => workstation.is_active && workstation.location?.is_active
  )
  const openSessions = openDrawersQuery.data?.data ?? []
  const selectedWorkstation = selectBalancingWorkstation({
    workstations,
    requestedWorkstationId: search.workstation_id,
    pairedWorkstationId,
  })
  const selectedSession = selectBalancingSession(openSessions, selectedWorkstation?.id)
  const moneyLink = path.money.index().using({ orgId }).link
  const reportPath = path.cashDrawer.index().using({ orgId })
  const reportLink = selectedWorkstation
    ? reportPath.search(getRegisterHistoryFilters(organization?.timezone, selectedWorkstation.id))
        .link
    : reportPath.link
  const balancingLink = path.cashDrawerBalancing.index().using({ orgId }).link
  const isRefreshing =
    statusQuery.isFetching || workstationsQuery.isFetching || openDrawersQuery.isFetching
  const selectWorkstation = (workstationId) =>
    navigate({
      ...balancingLink,
      search: { workstation_id: Number(workstationId) },
    })
  const retry = async () => {
    const [statusResult, workstationsResult, openDrawersResult] = await Promise.all([
      statusQuery.refetch(),
      workstationsQuery.refetch(),
      openDrawersQuery.refetch(),
    ])

    if (statusResult.isError || workstationsResult.isError || openDrawersResult.isError) {
      toast.error('Unable to refresh the registers', {
        description: 'Check your connection and try again.',
      })
    }
  }

  return (
    <div className="@container max-w-4xl space-y-5 pb-10">
      <AdminPageHeader
        icon={Banknote}
        title="Cash Drawer Balancing"
        description="Start a cash drawer, count it, and review earlier counts for any register."
        actionsClassName="items-center gap-x-5"
        actions={
          <>
            {/* The register is the page's scope: switching it replaces everything below. */}
            {trackingEnabled && selectedWorkstation && workstations.length > 1 ? (
              <Select value={String(selectedWorkstation.id)} onValueChange={selectWorkstation}>
                <SelectTrigger aria-label="Register" className="max-w-60">
                  <SelectValue placeholder="Choose a register" />
                </SelectTrigger>
                <SelectContent>
                  {workstations.map((option) => (
                    <SelectItem key={option.id} value={String(option.id)}>
                      {option.name} · {option.location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Can I="list" a="cash-drawer-reports">
              <Button asChild variant="link" size="inline">
                <Link {...reportLink}>
                  <History aria-hidden="true" className="size-4" />
                  Cash Drawer Report
                </Link>
              </Button>
            </Can>
          </>
        }
      />

      {renderBalancingContent({
        isRefreshing,
        moneyLink,
        onAdjust: () => setAdjustingSessionId(selectedSession?.id ?? null),
        requestedSessionId: search.session_id,
        openingMode: search.view === 'open',
        onSelectSession: (sessionId, view, replace = false) =>
          navigate({
            ...balancingLink,
            replace,
            search: { workstation_id: selectedWorkstation.id, session_id: sessionId, view },
          }),
        openDrawersQuery,
        openSessions,
        organizationTimezone: organization?.timezone,
        retry,
        selectedSession,
        selectedWorkstation,
        statusQuery,
        workstations,
        workstationsQuery,
        canViewHistory,
        orgId,
      })}

      {adjustingSessionId && selectedSession?.id === adjustingSessionId ? (
        <AdjustDrawerDialog
          key={`adjust-${selectedSession.id}`}
          open
          onOpenChange={(open) => !open && setAdjustingSessionId(null)}
          session={selectedSession}
        />
      ) : null}
    </div>
  )
}

function renderBalancingContent({
  isRefreshing,
  moneyLink,
  onAdjust,
  requestedSessionId,
  openingMode,
  onSelectSession,
  openDrawersQuery,
  organizationTimezone,
  retry,
  selectedSession,
  selectedWorkstation,
  statusQuery,
  workstations,
  workstationsQuery,
  canViewHistory,
  orgId,
}) {
  if (
    (statusQuery.isLoading && !statusQuery.data) ||
    (workstationsQuery.isLoading && !workstationsQuery.data) ||
    (openDrawersQuery.isLoading && !openDrawersQuery.data)
  ) {
    return <LoadingState label="Loading registers…" size="spacious" surface="card" />
  }

  if (
    (statusQuery.isError && !statusQuery.data) ||
    (workstationsQuery.isError && !workstationsQuery.data) ||
    (openDrawersQuery.isError && !openDrawersQuery.data)
  ) {
    return (
      <InfoNotice
        title="The registers are unavailable"
        tone="danger"
        action={
          <Button type="button" variant="notice" size="xs" disabled={isRefreshing} onClick={retry}>
            <RefreshCw className={isRefreshing ? 'size-4 animate-spin' : 'size-4'} />
            {isRefreshing ? 'Refreshing…' : 'Try again'}
          </Button>
        }
      >
        Check your connection and try again.
      </InfoNotice>
    )
  }

  if (statusQuery.data?.enabled !== true) {
    return (
      <InfoNotice
        title="Cash drawer tracking is off"
        tone="neutral"
        action={<InfoNoticeAction {...moneyLink}>Back to Money</InfoNoticeAction>}
      >
        Turn on cash drawer tracking before balancing a cash drawer.
      </InfoNotice>
    )
  }

  if (workstations.length === 0) {
    return (
      <InfoNotice
        title="There are no active registers"
        tone="neutral"
        action={<InfoNoticeAction {...moneyLink}>Back to Money</InfoNoticeAction>}
      >
        Add or reactivate a register in Cash Drawer settings.
      </InfoNotice>
    )
  }

  return (
    <CashDrawerRegisterBalancing
      key={selectedWorkstation.id}
      isRefreshing={isRefreshing}
      canViewHistory={canViewHistory}
      onAdjust={onAdjust}
      onRefresh={retry}
      requestedSessionId={requestedSessionId}
      openingMode={openingMode}
      onSelectSession={onSelectSession}
      orgId={orgId}
      session={selectedSession}
      timezone={organizationTimezone}
      workstation={selectedWorkstation}
    />
  )
}
